import { ZeusHelpButton } from "@/components/zeus-help-dialog";
import { useState, useCallback, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { AutoExpandingTextarea } from "@/components/auto-expanding-textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Trash2, GripVertical, Plus, AlertCircle } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Grateful } from "@shared/schema";

interface SortableGratefulItemProps {
  grateful: Grateful;
  index: number;
  onTextChange: (id: string, text: string) => void;
  onDelete: (id: string) => void;
  onTextBlur: (id: string, text: string) => void;
}

function SortableGratefulItem({ 
  grateful, 
  index, 
  onTextChange, 
  onDelete,
  onTextBlur 
}: SortableGratefulItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: grateful.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onTextChange(grateful.id, e.target.value);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 sm:gap-4 p-2 sm:p-4 bg-card border rounded-lg"
      data-testid={`grateful-item-${grateful.id}`}
    >
      {/* Static number */}
      <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs sm:text-sm font-medium mt-1">
        {index + 1}
      </div>

      <div
        {...attributes}
        {...listeners}
        className="flex-shrink-0 cursor-grab active:cursor-grabbing p-0.5 sm:p-1 hover:bg-muted rounded mt-1"
        data-testid={`drag-handle-${grateful.id}`}
      >
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </div>

      <AutoExpandingTextarea
        value={grateful.content}
        onChange={handleChange}
        onBlur={(e) => onTextBlur(grateful.id, e.target.value)}
        placeholder="What are you grateful for today?"
        className="flex-1 min-w-0 min-h-[40px]"
        minRows={1}
        maxRows={10}
        data-testid={`textarea-grateful-${grateful.id}`}
      />

      <Button
        variant="ghost"
        size="sm"
        onClick={() => onDelete(grateful.id)}
        className="flex-shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10 mt-1 p-1 sm:p-2"
        data-testid={`button-delete-${grateful.id}`}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

export default function GratefulsPage() {
  const [localGratefuls, setLocalGratefuls] = useState<Grateful[]>([]);
  const prevGratefulsRef = useRef<Grateful[]>([]);

  // Fetch gratefuls
  const { data: gratefuls = [], isLoading, error } = useQuery<Grateful[]>({
    queryKey: ["/api/gratefuls"],
  });

  // Initialize local state when data loads
  useEffect(() => {
    // Only update if the data has actually changed (deep comparison by stringifying)
    const currentData = JSON.stringify(gratefuls);
    const prevData = JSON.stringify(prevGratefulsRef.current);
    
    if (currentData !== prevData) {
      setLocalGratefuls(gratefuls);
      prevGratefulsRef.current = gratefuls;
    }
  }, [gratefuls]);

  // Create grateful mutation
  const createMutation = useMutation({
    mutationFn: (data: { content: string }) => apiRequest("POST", "/api/gratefuls", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gratefuls"] });
    },
    onError: () => {
    },
  });

  // Update grateful mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Grateful> }) =>
      apiRequest("PATCH", `/api/gratefuls/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gratefuls"] });
    },
    onError: () => {
      setLocalGratefuls(gratefuls);
    },
  });

  // Delete grateful mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/gratefuls/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gratefuls"] });
    },
    onError: () => {
      setLocalGratefuls(gratefuls);
    },
  });

  // Reorder gratefuls mutation
  const reorderMutation = useMutation({
    mutationFn: (gratefulOrders: { id: string; displayOrder: number }[]) =>
      apiRequest("PATCH", "/api/gratefuls/reorder", { gratefulOrders }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gratefuls"] });
    },
    onError: () => {
      setLocalGratefuls(gratefuls);
    },
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle text changes (optimistic update)
  const handleTextChange = useCallback((id: string, content: string) => {
    setLocalGratefuls(prev => 
      prev.map(grateful => 
        grateful.id === id ? { ...grateful, content } : grateful
      )
    );
  }, []);

  // Handle text blur (save changes)
  const handleTextBlur = useCallback((id: string, content: string) => {
    const original = gratefuls.find(g => g.id === id);
    if (original && original.content !== content) {
      updateMutation.mutate({ id, data: { content } });
    }
  }, [gratefuls, updateMutation]);

  // Handle drag end
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localGratefuls.findIndex(item => item.id === active.id);
      const newIndex = localGratefuls.findIndex(item => item.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(localGratefuls, oldIndex, newIndex);
        setLocalGratefuls(newOrder);
        
        // Create gratefulOrders with updated display orders
        const gratefulOrders = newOrder.map((grateful, index) => ({
          id: grateful.id,
          displayOrder: index
        }));
        reorderMutation.mutate(gratefulOrders);
      }
    }
  }, [localGratefuls, reorderMutation]);

  // Handle add grateful
  const handleAddGrateful = useCallback(() => {
    createMutation.mutate({ content: "" });
  }, [createMutation]);

  // Handle delete grateful
  const handleDeleteGrateful = useCallback((id: string) => {
    setLocalGratefuls(prev => prev.filter(grateful => grateful.id !== id));
    deleteMutation.mutate(id);
  }, [deleteMutation]);

  if (isLoading) {
    return (
      <div className="w-full max-w-full p-4 md:p-6">
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Gratefuls</h1>
            <p className="text-muted-foreground mt-1">
              Express what you're grateful for today
            </p>
          </div>
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-start gap-4">
                    <Skeleton className="w-8 h-8 rounded-full" />
                    <Skeleton className="w-6 h-6" />
                    <Skeleton className="flex-1 h-10" />
                    <Skeleton className="w-8 h-8" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-full p-4 md:p-6">
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Gratefuls</h1>
            <p className="text-muted-foreground mt-1">
              Express what you're grateful for today
            </p>
          </div>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load gratefuls. Please refresh the page or try again later.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full p-4 md:p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-foreground" data-testid="page-title">
              Gratefuls
            </h1>
            <p className="text-muted-foreground mt-1">
              Express what you're grateful for today. Drag to reorder.
            </p>
          </div>
          <ZeusHelpButton focus="gratefuls" />
        </div>

        {/* Gratefuls List */}
        <Card>
          <CardHeader>
            <CardTitle>Your Gratefuls</CardTitle>
            <CardDescription>
              Write down what you're grateful for. Each entry can expand up to 10 lines.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {localGratefuls.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">
                  You haven't added any gratefuls yet. Start by adding your first grateful.
                </p>
                <Button onClick={handleAddGrateful} data-testid="button-add-first-grateful">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Grateful
                </Button>
              </div>
            ) : (
              <>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext 
                    items={localGratefuls.map(g => g.id)} 
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3" data-testid="gratefuls-list">
                      {localGratefuls.map((grateful, index) => (
                        <SortableGratefulItem
                          key={grateful.id}
                          grateful={grateful}
                          index={index}
                          onTextChange={handleTextChange}
                          onDelete={handleDeleteGrateful}
                          onTextBlur={handleTextBlur}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>

                <div className="pt-4 border-t">
                  <Button 
                    onClick={handleAddGrateful}
                    variant="outline"
                    className="w-full"
                    disabled={createMutation.isPending}
                    data-testid="button-add-grateful"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {createMutation.isPending ? "Adding..." : "Add More"}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Tips */}
        <Card>
          <CardHeader>
            <CardTitle>Benefits of Gratitude Practice</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Improves mental health and reduces stress</li>
              <li>• Enhances sleep quality and overall well-being</li>
              <li>• Strengthens relationships and social bonds</li>
              <li>• Increases resilience and emotional strength</li>
              <li>• Practice daily for best results</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
